import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { toast } from 'react-toastify';
import {
  SingleComboBox,
  Input,
  InputCheckBox,
  FormControlText,
  Flex,
  Grid,
  TextLink
} from '@itandi/itandi-bb-ui';
import { useFormContext, useWatch } from 'react-hook-form';
import type { DealParam, DealUpdateParam } from '~/models/DealForm';
import { FormRow } from './FormRow';
import { usePropertySelectHandler } from '~/hooks/deals/useRoomBlockPropertySelect';
import type { Deal } from '~/models/Deal';
import { usePropertyBuildingOptions } from '~/hooks/usePropertyBuildingOptions';
import { usePropertyRoomOptions } from '~/hooks/usePropertyRoomOptions';
import type { PropertyBuildingOption } from '~/models/PropertyBuildingOption';
import {
  buildingOptions,
  propertyBuildingOptionValue,
  buildPropertyBuildingOptionsFromRoomName
} from '~/models/PropertyBuildingOption';
import type { PropertyRoomOption } from '~/models/PropertyRoomOption';
import {
  roomOptions,
  propertyRoomOptionValue,
  buildPropertyRoomOptionsFromRoomNumber
} from '~/models/PropertyRoomOption';
import {
  editRentRoomBuildingUrl,
  editRentRoomUrl,
  editRentParkingSpaceUrl
} from '~/utils/BukkenKanriPath';
import { useDebounce } from '~/hooks/useDebounce';
import { ItandiAccountContext } from '~/models/ItandiAccount';

type Props = { deal: Deal };

export const RoomBlockPropertySelectV2: React.FC<Props> = ({ deal }) => {
  const { company } = useContext(ItandiAccountContext);
  // KT経由で作成された案件はBKK以外の物件も保存されていることがあるため、この変数はfalseにする
  // trueのままだと、部屋番号の選択肢が返ってこず(BE側でBKK物件しか選択肢として返さなくなるため)、「棟または戸建てとして登録」に自動チェックが入ってしまう
  const showOnlyAmaterasProperties =
    !!company?.onlyAmaterasProperties && !deal.leasingId;
  const {
    register,
    control,
    formState: { errors },
    setValue
  } = useFormContext<DealParam | DealUpdateParam>();
  const isRegisterAsBuilding = useWatch({
    control,
    name: 'isRegisterAsBuilding'
  });
  const debounce = useDebounce(200);
  const [inputRoomName, setInputRoomName] = useState<string>('');
  const [inputRoomNumber, setInputRoomNumber] = useState<string>('');
  const [selectedBuilding, setSelectedBuilding] =
    React.useState<PropertyBuildingOption | null>(
      deal.propertyBuildingOption ?? null
    );
  const [selectedRoom, setSelectedRoom] =
    React.useState<PropertyRoomOption | null>(deal.propertyRoomOption ?? null);
  const handleInputChangeBuilding = useCallback(
    (text: string) =>
      debounce(() => {
        setInputRoomName(text.trim());
      }),
    [debounce, setInputRoomName]
  );
  const handleInputChangeRoom = useCallback(
    (text: string) => setInputRoomNumber(text.trim()),
    [setInputRoomNumber]
  );
  // フォームに表示する物件名と部屋番号の候補を取得
  const {
    data: propertyBuildingOptionsFromApi,
    isValidating: isPropertyBuildingOptionsValidating
  } = usePropertyBuildingOptions().index(inputRoomName);
  const {
    data: propertyRoomOptionsFromApi,
    isValidating: isPropertyRoomOptionsValidating
  } = usePropertyRoomOptions().index(
    selectedBuilding?.name ?? null,
    selectedBuilding?.address ?? null,
    selectedBuilding?.amaterasPropertyId ?? null
  );

  const propertyBuildingOptions = useMemo(() => {
    const roomNameOptions = showOnlyAmaterasProperties
      ? []
      : buildPropertyBuildingOptionsFromRoomName(inputRoomName);

    return [...roomNameOptions, ...(propertyBuildingOptionsFromApi ?? [])];
  }, [
    showOnlyAmaterasProperties,
    inputRoomName,
    propertyBuildingOptionsFromApi
  ]);
  const propertyRoomOptions = useMemo(() => {
    const roomNumberOptions = showOnlyAmaterasProperties
      ? []
      : buildPropertyRoomOptionsFromRoomNumber(inputRoomNumber);

    return [...roomNumberOptions, ...(propertyRoomOptionsFromApi ?? [])];
  }, [showOnlyAmaterasProperties, inputRoomNumber, propertyRoomOptionsFromApi]);
  // BKKのみフラグがONで部屋が登録されていないBKK物件を選択した時、自動で棟または戸建てとして登録にチェックを入れる
  const shouldDisableRegisterAsBuilding =
    showOnlyAmaterasProperties &&
    !!selectedBuilding &&
    !isPropertyRoomOptionsValidating &&
    propertyRoomOptions.length === 0;
  const shouldDisableAddress =
    !!selectedBuilding?.amaterasPropertyId && !!selectedBuilding.address;
  // KTで駐車場区分が紐づいている契約でNSKの案件作成に訪れたとき、BKK駐車場区画へのリンクを出す対応
  const showParkingSpaceProductLink = deal.propertyType === 'parking_space';

  const {
    onChangeBuilding,
    onChangeRoom,
    onChangeRegisterAsBuildingCheck,
    onBlurBuilding,
    onBlurRoom
  } = usePropertySelectHandler({
    setValue,
    propertyBuildingOptions,
    propertyRoomOptions,
    selectedBuilding,
    selectedRoom,
    setSelectedBuilding,
    setSelectedRoom
  });

  useEffect(() => {
    if (!isRegisterAsBuilding && shouldDisableRegisterAsBuilding) {
      onChangeRegisterAsBuildingCheck(true);
      toast.success(
        '部屋の登録がない物件のため、「棟または戸建てとして登録」にチェックを入れました。'
      );
    }
  }, [
    isRegisterAsBuilding,
    shouldDisableRegisterAsBuilding,
    onChangeRegisterAsBuildingCheck
  ]);

  return (
    <>
      <FormRow error={errors.roomName?.message} label="物件名" required>
        <Grid alignItems="center" gap=".5rem" templateColumns="1fr 11rem">
          <SingleComboBox
            isInvalid={errors.roomName != null}
            loading={isPropertyBuildingOptionsValidating}
            onBlur={onBlurBuilding}
            onChange={onChangeBuilding}
            onInputChange={handleInputChangeBuilding}
            options={buildingOptions(propertyBuildingOptions)}
            placeholder="例）サンプルマンション"
            value={propertyBuildingOptionValue(selectedBuilding)}
            withAsyncOptions
          />
          {selectedBuilding?.amaterasPropertyId && (
            <TextLink
              color="link"
              href={editRentRoomBuildingUrl(
                selectedBuilding.amaterasPropertyId
              )}
              target="_blank"
            >
              物件システム
            </TextLink>
          )}
        </Grid>
        <FormControlText help="2文字以上入力すると候補が最大100件まで表示されます。" />
        <FormControlText help="「・」などの記号を含む物件名がヒットしない場合は、記号を省いたフリガナで検索をお試しください。" />
      </FormRow>
      <FormRow error={errors.roomNumber?.message} label="部屋番号" required>
        <Flex direction="column" gap=".5rem">
          <InputCheckBox
            checked={isRegisterAsBuilding}
            disabled={shouldDisableRegisterAsBuilding}
            label="棟または戸建てとして登録"
            onChange={(event): void => {
              const { checked } = event.target;
              onChangeRegisterAsBuildingCheck(checked);
            }}
          />
          <Grid alignItems="center" gap=".5rem" templateColumns="1fr 11rem">
            <SingleComboBox
              isDisabled={isRegisterAsBuilding}
              isInvalid={errors.roomNumber != null}
              loading={isPropertyRoomOptionsValidating}
              onBlur={onBlurRoom}
              onChange={onChangeRoom}
              onInputChange={handleInputChangeRoom}
              options={roomOptions(propertyRoomOptions)}
              placeholder="例）101"
              value={propertyRoomOptionValue(selectedRoom)}
              withAsyncOptions
            />
            {selectedRoom?.amaterasPropertyId && (
              <TextLink
                color="link"
                href={
                  showParkingSpaceProductLink
                    ? editRentParkingSpaceUrl(selectedRoom.amaterasPropertyId)
                    : editRentRoomUrl(selectedRoom.amaterasPropertyId)
                }
                target="_blank"
              >
                物件システム
              </TextLink>
            )}
          </Grid>
        </Flex>
      </FormRow>

      <FormRow error={errors.address?.message} label="物件住所" required>
        <Input
          disabled={shouldDisableAddress}
          isInvalid={errors.address != null}
          placeholder="例）東京都港区六本木1-2-3"
          {...register('address')}
        />
      </FormRow>
    </>
  );
};
